import ModalBackdrop from '@/components/ModalBackdrop';
import { X, ExternalLink } from 'lucide-react';
import { useEffect } from 'react';
import OfficialSourcesNotice from '@/components/OfficialSourcesNotice';
import { lockScroll, unlockScroll } from '@/utils/scrollLock';
import webPackage from '../../package.json';

interface AboutModalProps {
  open: boolean;
  onClose: () => void;
}

export default function AboutModal({ open, onClose }: AboutModalProps) {
  // Lock scroll when modal is open
  useEffect(() => {
    if (open) {
      lockScroll();
      return () => unlockScroll();
    }
  }, [open]);

  if (!open) return null;

  const currentYear = new Date().getFullYear();
  const appVersion = webPackage.version;
  const environment = import.meta.env.VITE_ENVIRONMENT || 'local';
  const environmentSuffix = environment === 'local' ? ' (local)' : environment === 'dev' ? ' (dev)' : '';

  return (
    <ModalBackdrop onClick={onClose} viewportCentered>
      <div 
        onClick={(e) => e.stopPropagation()}
        className="relative bg-card rounded-xl p-6 w-full mx-4 shadow-xl border border-border max-h-[90vh] overflow-y-auto max-w-md"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-lg hover:bg-muted transition-colors z-10"
          aria-label="Stäng"
        >
          <X className="h-5 w-5" />
        </button>

        {/* About view */}
        <div className="flex flex-col items-center gap-4 pt-2">
          {/* Logo with glow and pulse animation */}
          <div className="relative">
            {/* Glowing background layer */}
            <div className="absolute inset-0 blur-2xl opacity-50 animate-pulse">
              <img 
                src="/logos/logo-color-textunder.svg" 
                alt=""
                className="h-48 w-auto"
              />
            </div>
            {/* Main logo */}
            <img 
              src="/logos/logo-color-textunder.svg" 
              alt="Beredskapshubben Logo"
              className="relative h-48 w-auto animate-pulse"
            />
          </div>

          {/* App info */}
          <div className="text-center space-y-2">
            <p className="text-sm text-muted-foreground">Version {appVersion}{environmentSuffix}</p>
          </div>

          {/* Description */}
          <div className="text-center space-y-3 text-sm text-muted-foreground">
            <p>
              Beredskapshubben hjälper dig och din familj, tillsammans med dina vänner, grannar och kollegor att bygga upp en god beredskap – steg för steg.
            </p>
          </div>

          <OfficialSourcesNotice
            compact
            className="w-full text-left"
            title="Viktigt att veta"
            description="Appen är ett privat stödverktyg. Kontrollera alltid aktuella råd och instruktioner mot officiella källor."
          />

          {/* Copyright and legal */}
          <div className="w-full border-t border-border pt-4 space-y-3 text-xs text-muted-foreground text-center">
            {/* External legal links */}
            <div className="flex flex-wrap gap-3 justify-center text-sm pt-2">
              <a 
                href="/terms" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors"
              >
                Användarvillkor
                <ExternalLink className="h-3 w-3" />
              </a>
              <span>•</span>
              <a 
                href="/privacy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors"
              >
                Integritetspolicy
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
            <div className="flex flex-wrap gap-3 justify-center text-sm pt-2">
              <a 
                href="/cookies" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors"
              >
                Cookiepolicy
                <ExternalLink className="h-3 w-3" />
              </a>
              <span>•</span>
              <a 
                href="/community-guidelines" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors"
              >
                Communityregler
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
            <div className="flex flex-wrap gap-3 justify-center text-sm pt-2">
              <p>
                © {currentYear}{' '}
                <a
                  href="https://beredskapshubben.se/about"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Beredskapshubben Norden AB
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </ModalBackdrop>
  );
}
